/**
 * A regra de mascara do contrato, do lado de quem preenche o formulario.
 *
 * `CivilNameInput` tem `civilName` — o nome inteiro, como a pessoa o escreve — e
 * `maskSplitIndex`, quantos tokens iniciais formam o nome de tratamento. A
 * mascara e os `maskSplitIndex` primeiros tokens mais a inicial significativa
 * do que sobrou, pulando particula: `("Joao Carlos da Silva Santos", 2)` rende
 * `"Joao Carlos S."`, porque `"d."` nao identifica ninguem.
 *
 * O calculo mora aqui, e nao dentro do componente, porque e ele que a tela
 * mostra enquanto a pessoa escolhe o corte: quem preenche precisa **ver** a
 * mascara que esta criando. O backend nao adivinha onde cortar, e corte errado
 * produz mascara errada — que e a regra central de privacidade da plataforma.
 */

/**
 * Preposicoes de sobrenome que nao servem como inicial. Espelha a lista de
 * `api/internal/identity/name.go`; as duas precisam concordar, senao a tela
 * promete uma mascara que o servidor nao produz.
 */
const PARTICULAS = new Set([
  'da',
  'das',
  'de',
  'del',
  'do',
  'dos',
  'e',
  'la',
  'van',
  'von',
  'y',
]);

/** Tokens do nome civil, na ordem escrita, sem espaco vazio. */
export function nameTokens(civilName: string): readonly string[] {
  return civilName
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0);
}

/**
 * A mascara que este corte produz, ou `null` quando o corte nao produz nenhuma:
 * indice fora da faixa, ou nome de um token so — sem token seguinte nao ha
 * inicial para abreviar.
 */
export function maskFor(civilName: string, maskSplitIndex: number): string | null {
  const tokens = nameTokens(civilName);
  if (maskSplitIndex < 1 || maskSplitIndex >= tokens.length) return null;

  const rest = tokens.slice(maskSplitIndex);
  // A inicial e a do primeiro token que identifica alguem. Sobrenome formado so
  // por particulas cai no primeiro token mesmo — sem inicial nao ha mascara.
  const significant = rest.find((token) => !PARTICULAS.has(token.toLowerCase())) ?? rest[0];
  if (significant === undefined || significant.length === 0) return null;

  return `${tokens.slice(0, maskSplitIndex).join(' ')} ${significant.charAt(0).toUpperCase()}.`;
}

/**
 * Cortes possiveis para este nome: de 1 ate o penultimo token. O ultimo token
 * nunca e corte valido porque nao sobraria nome nenhum para abreviar.
 */
export function splitOptionsFor(civilName: string): readonly number[] {
  const tokens = nameTokens(civilName);
  if (tokens.length < 2) return [];
  return tokens.slice(1).map((_, index) => index + 1);
}

/**
 * Mantem o corte dentro da faixa valida quando o nome muda debaixo dele.
 * Escrever mais um sobrenome nao pode invalidar em silencio a escolha ja feita,
 * e apagar palavras nao pode deixar um indice apontando para o vazio.
 */
export function clampSplitIndex(civilName: string, maskSplitIndex: number): number {
  const options = splitOptionsFor(civilName);
  if (options.length === 0) return 1;
  const last = options[options.length - 1] as number;
  if (maskSplitIndex < 1) return 1;
  return Math.min(maskSplitIndex, last);
}
